import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Brain, ArrowLeft, Send, Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RequestAccess() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', profession: '', interest_reason: '', referred_by: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.profession || !form.interest_reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.referred_by) payload.referred_by = null;
      await axios.post(`${API_URL}/api/waitlist/request`, payload);
      setSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || 'Failed to submit request';
      if (msg.includes('already')) {
        toast.info(msg);
        setSubmitted(true);
      } else {
        toast.error(msg);
      }
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div className="text-center max-w-md space-y-6" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
            <CheckCircle2 className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight">Request Submitted</h2>
          <p className="text-muted-foreground leading-relaxed">
            Your access request has been received. We will review it and get back to you shortly.
          </p>
          <Link to="/">
            <Button variant="outline" data-testid="back-home-btn">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="request-access-page">
      <motion.div className="w-full max-w-lg space-y-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Logo */}
        <div className="text-center space-y-2">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <Brain className="h-6 w-6 text-primary-foreground" strokeWidth={1.5} />
            </div>
            <span className="text-2xl font-semibold tracking-tight">MindFlo</span>
          </Link>
          <p className="text-muted-foreground text-sm">Request access to the platform</p>
        </div>

        <Card className="bg-card border-border/50">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Full Name <span className="text-destructive">*</span></Label>
                  <Input value={form.name} onChange={e => update('name', e.target.value)} placeholder="Your full name" required data-testid="ra-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Email Address <span className="text-destructive">*</span></Label>
                  <Input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@example.com" required data-testid="ra-email" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Phone Number <span className="text-destructive">*</span></Label>
                  <Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 (555) 000-0000" required data-testid="ra-phone" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Profession <span className="text-destructive">*</span></Label>
                  <Input value={form.profession} onChange={e => update('profession', e.target.value)} placeholder="e.g. Psychologist, Coach" required data-testid="ra-profession" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Why are you interested in MindFlo? <span className="text-destructive">*</span></Label>
                <Textarea value={form.interest_reason} onChange={e => update('interest_reason', e.target.value)} placeholder="Tell us about your interest in cognitive architecture..." rows={3} required data-testid="ra-interest" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Referred By <span className="text-muted-foreground">(optional)</span></Label>
                <Input value={form.referred_by} onChange={e => update('referred_by', e.target.value)} placeholder="Name of person who referred you" data-testid="ra-referred" />
              </div>

              <Button type="submit" className="w-full glow-primary" disabled={loading} data-testid="ra-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline font-medium">Login</Link>
        </p>
      </motion.div>
    </div>
  );
}
